import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useContent } from '@/stores/RootStore';
import { useReveal } from '@/hooks/useReveal';
import { scrollToSection } from '@/hooks/useLenis';
import SubPageHero from '@/components/SubPageHero';
import s from './Support.module.scss';

// 고객지원 (/support) — 목업 evs.com/contact(문의 폼) + evstech 견적문의 실측 융합.
//   디자인 = 기존 리엑트(라이트+네이비+시안, SubPageHero). 섹션: 견적문의(#inquiry) · 오시는 길(#location).
//   ★문의 폼은 프론트 데모(백엔드 미연동) — 제출 시 접수 확인 UI만 표시. 실제 전송은 추후 WebSocket/API 연동.

const Reveal = ({ id, className = '', children }: { id?: string; className?: string; children: React.ReactNode }) => {
  const ref = useReveal<HTMLDivElement>();
  return <div id={id} className={`${className} anim-scroll`} ref={ref}>{children}</div>;
};

const TelIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" /></svg>;
const MailIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>;
const CheckIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>;
const DocIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M8 13h8M8 17h5" /></svg>;
const DownloadIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12m0 0 4-4m-4 4-4-4M4 21h16" /></svg>;

const InquiryForm = observer(() => {
  const { support } = useContent();
  const [sent, setSent] = useState(false);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO(backend): API/WebSocket 전송. 현재는 프론트 데모 — 접수 확인 UI만 표시.
    setSent(true);
  };
  if (sent) {
    return (
      <div className={s.form}>
        <div className={s.thanks}>
          <span className={s.thanksIcon}><CheckIcon /></span>
          <h3 className={s.thanksTitle}>문의가 접수되었습니다</h3>
          <p className={s.thanksBody}>담당 영업팀이 확인 후 빠르게 연락드리겠습니다.<br />급하신 경우 위 연락처로 직접 문의해 주세요.</p>
        </div>
      </div>
    );
  }
  return (
    <form className={s.form} onSubmit={submit}>
      <div className={s.formRow}>
        <div className={s.field}>
          <label className={s.label}>이름<span className={s.req}>*</span></label>
          <input className={s.input} type="text" required placeholder="홍길동" />
        </div>
        <div className={s.field}>
          <label className={s.label}>회사명</label>
          <input className={s.input} type="text" placeholder="(주)회사명" />
        </div>
      </div>
      <div className={s.formRow}>
        <div className={s.field}>
          <label className={s.label}>이메일<span className={s.req}>*</span></label>
          <input className={s.input} type="email" required placeholder="name@company.com" />
        </div>
        <div className={s.field}>
          <label className={s.label}>연락처<span className={s.req}>*</span></label>
          <input className={s.input} type="tel" required placeholder="010-0000-0000" />
        </div>
      </div>
      <div className={s.field}>
        <label className={s.label}>문의 유형<span className={s.req}>*</span></label>
        <select className={s.select} required defaultValue="">
          <option value="" disabled>문의 유형을 선택해 주세요</option>
          {support.form.subjects.map((sub) => <option key={sub} value={sub}>{sub}</option>)}
        </select>
      </div>
      <div className={s.field}>
        <label className={s.label}>문의 내용<span className={s.req}>*</span></label>
        <textarea className={s.textarea} required placeholder="도입 규모, 설치 현장, 필요 사양 등을 남겨주시면 더 정확히 안내드립니다." />
      </div>
      <label className={s.checkRow}>
        <input type="checkbox" required />
        <span className={s.checkText}>개인정보 수집·이용에 동의합니다. {support.form.privacyNote}</span>
      </label>
      <button type="submit" className={s.submit}>문의 보내기</button>
    </form>
  );
});

const Support = observer(() => {
  const { support, location } = useContent();
  const { hero, intro, contacts, resources } = support;
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    const id = hash.replace('#', '');
    let cancelled = false;
    const run = () => { if (!cancelled) requestAnimationFrame(() => scrollToSection(id)); };
    if (document.fonts?.ready) document.fonts.ready.then(run); else run();
    return () => { cancelled = true; };
  }, [hash]);

  return (
    <main>
      <SubPageHero
        title={hero.slogan}
        /* [2026-10-11 사용자] 히어로 서브 슬로건 풍성화: 기존 '무엇을 도와드릴까요?'는 너무 짧음 → 지원 범위(견적·설치·기술지원·A/S·자료·FAQ)를 담아 페이지 내용과 관련성 강화. */
        subtitle="무엇을 도와드릴까요? 제품 견적·설치·기술 지원부터 A/S·자료실·FAQ까지, EVS 고객지원팀이 신속히 돕겠습니다."
        image="/images/support/support-hero.webp"
        crumbs={[{ label: 'HOME' }, { label: '고객지원' }]}
      />

      {/* 견적문의 — 안내+연락처 / 문의 폼 */}
      <section id="inquiry" className={s.section}>
        <div className={s.inner}>
          <div className={s.inquiry}>
            <Reveal className={s.leadCol}>
              <p className={s.eyebrow}>CONTACT US</p>
              <h2 className={s.title}>{intro.lead}</h2>
              <p className={s.body}>{intro.body}</p>
              <ul className={s.contactCards}>
                {contacts.map((c) => (
                  <li key={c.team} className={s.contactCard}>
                    <span className={s.ccTeam}>{c.team}</span>
                    <p className={s.ccRow}><TelIcon /><a href={`tel:${c.tel.replace(/[^0-9]/g, '')}`}>{c.tel}</a></p>
                    <p className={s.ccRow}><MailIcon /><a href={`mailto:${c.email}`}>{c.email}</a></p>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal>
              <InquiryForm />
            </Reveal>
          </div>
        </div>
      </section>

      {/* 자료실 — 목업(다운로드 준비 중) */}
      <section id="resources" className={`${s.section} ${s.alt}`}>
        <div className={s.inner}>
          <Reveal>
            <p className={s.eyebrow}>RESOURCES</p>
            <h2 className={s.title}>{resources.lead}</h2>
            <p className={s.body}>{resources.body}</p>
          </Reveal>
          <Reveal className={s.resList}>
            {resources.items.map((r) => (
              <div className={s.resCard} key={r.title}>
                <span className={s.resIcon}><DocIcon /></span>
                <div className={s.resMeta}>
                  <h3 className={s.resTitle}>{r.title}</h3>
                  <p className={s.resDesc}>{r.desc}</p>
                </div>
                <span className={s.resType}>{r.type}</span>
                <button type="button" className={s.resBtn} disabled aria-label="준비 중">
                  <DownloadIcon /><span>준비 중</span>
                </button>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* 오시는 길 — 회사소개와 동일 데이터/임베드 방식 */}
      <section id="location" className={s.section}>
        <div className={s.inner}>
          <Reveal>
            <p className={s.eyebrow}>LOCATION</p>
            <h2 className={s.title}>오시는 길</h2>
            <p className={s.body}>{location.tagline}</p>
          </Reveal>
          <Reveal className={s.locGrid}>
            {location.offices.map((o) => {
              const src = 'mapEmbed' in o && o.mapEmbed
                ? o.mapEmbed
                : `https://maps.google.com/maps?q=${encodeURIComponent((o as { mapQuery: string }).mapQuery)}&z=16&hl=ko&output=embed`;
              return (
                <div className={s.office} key={o.label}>
                  <div className={s.map}>
                    <iframe src={src} title={`${o.label} 지도`} loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" />
                  </div>
                  <div className={s.officeInfo}>
                    <span className={s.officeLabel}>{o.label}</span>
                    <p className={s.officeAddr}>{o.address}</p>
                    <p className={s.officeTel}>Tel. {o.tel}</p>
                  </div>
                </div>
              );
            })}
          </Reveal>
        </div>
      </section>
    </main>
  );
});

export default Support;
